import React from 'react';
import { Button } from 'antd';

import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { downloadFile, downloadFileByObjectURL } from 'utils/fileUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  getCustomersDetailExcel,
  getCustomerCertificateOfInsuranceCreatedFile,
  getCustomerTempToPermConversionFileInfoForCustomer,
  getServiceAgreementFileInfoForCustomer,
} from 'redux/reducers/clientSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../ClientDetailsPage.module.less';

type Props = {
  clientId?: string;
  hasAgreementAttached: boolean;
  hasCertificateOfInsurance: boolean;
  hasTempToPermConversion: boolean;
};

export default function DownloadClient(props: Props) {
  const {
    clientId,
    hasAgreementAttached,
    hasCertificateOfInsurance,
    hasTempToPermConversion,
  } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();

  const handleDownloadClient = async () => {
    const result = await dispatch(
      getCustomersDetailExcel({ customerId: clientId as string, isAdminRole }),
    );

    if (getCustomersDetailExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);

      await downloadClientAttachedFiles();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadServiceAgreementFileInfoForCustomer = async () => {
    const result = await dispatch(
      getServiceAgreementFileInfoForCustomer({
        customerId: clientId as string,
        isAdminRole,
      }),
    );

    if (getServiceAgreementFileInfoForCustomer.fulfilled.match(result)) {
      return result.payload;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      return Promise.reject();
    }
  };

  const loadCustomerCertificateOfInsuranceCreatedFile = async () => {
    const result = await dispatch(
      getCustomerCertificateOfInsuranceCreatedFile({
        customerId: clientId as string,
        isAdminRole,
      }),
    );

    if (getCustomerCertificateOfInsuranceCreatedFile.fulfilled.match(result)) {
      return result.payload;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      return Promise.reject();
    }
  };

  const loadCustomerTempToPermConversionFileInfoForCustomer = async () => {
    const result = await dispatch(
      getCustomerTempToPermConversionFileInfoForCustomer({
        customerId: clientId as string,
        isAdminRole,
      }),
    );

    if (
      getCustomerTempToPermConversionFileInfoForCustomer.fulfilled.match(result)
    ) {
      return result.payload;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      return Promise.reject();
    }
  };

  const downloadClientFile = async (
    downloadResponse: ResourceDownloadResponse,
  ) => {
    try {
      const { downloadUrl, fileName } = downloadResponse;

      await downloadFile({ downloadUrl, fileName });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const downloadClientAttachedFiles = async () => {
    let downloadRequests: Promise<ResourceDownloadResponse>[] = [];

    hasAgreementAttached &&
      downloadRequests.push(loadServiceAgreementFileInfoForCustomer());

    hasCertificateOfInsurance &&
      downloadRequests.push(loadCustomerCertificateOfInsuranceCreatedFile());

    hasTempToPermConversion &&
      downloadRequests.push(
        loadCustomerTempToPermConversionFileInfoForCustomer(),
      );

    const responses = await Promise.allSettled(downloadRequests);
    const payloads: ResourceDownloadResponse[] = responses
      .filter((response) => response.status === 'fulfilled')
      .map((response) => response.value);

    Promise.allSettled(payloads.map(downloadClientFile));
  };

  return (
    <Button
      className={styles.downloadBtn}
      onClick={handleDownloadClient}
      icon={<span className="icon icon-xls-file" />}
    />
  );
}
